import React, { useState, useEffect, useCallback } from 'react';
import {
    X, Clock, Save, Info, ChevronDown, ChevronUp, Users, Briefcase,
    Building2, Globe, AlertTriangle, Eye, Loader2, Check
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const CourseCreationModal = ({ isOpen, onClose, onSuccess, courseType, course }) => {
    const isEditing = !!course;

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        duration_days: 30,
        enrollment_method: 'manual', // 'automatic' or 'manual'
        matching_logic: 'any', // 'any' (OR) or 'all' (AND)
        lifecycle_stages: [],
        roles: [],
        teams: { ids: [], scope: 'all' },
        company_wide: false
    });

    const [loading, setLoading] = useState(false);
    const [expandedSections, setExpandedSections] = useState({
        lifecycle: false,
        roles: false,
        teams: false,
        companyWide: false
    });

    // Data for dropdowns
    const [availableRoles, setAvailableRoles] = useState([]);
    const [loadingRoles, setLoadingRoles] = useState(false);

    // Preview state
    const [previewCount, setPreviewCount] = useState(0);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [previewEmployees, setPreviewEmployees] = useState([]);

    useEffect(() => {
        if (isOpen) {
            fetchRoles();
            if (course) {
                // Parse existing rules
                const rules = typeof course.auto_enrollment_rules === 'string'
                    ? JSON.parse(course.auto_enrollment_rules || '{}')
                    : course.auto_enrollment_rules || {};

                setFormData({
                    name: course.name || '',
                    description: course.description || '',
                    duration_days: course.duration_days || 30,
                    enrollment_method: course.auto_enrollment_enabled ? 'automatic' : 'manual',
                    matching_logic: rules.matching_logic || 'any',
                    lifecycle_stages: rules.lifecycle_stages || [],
                    roles: rules.roles || [],
                    teams: rules.teams || { ids: [], scope: 'all' },
                    company_wide: rules.company_wide || false
                });
            } else {
                setFormData({
                    name: '',
                    description: '',
                    duration_days: 30,
                    enrollment_method: 'manual',
                    matching_logic: 'any',
                    lifecycle_stages: [],
                    roles: [],
                    teams: { ids: [], scope: 'all' },
                    company_wide: false
                });
            }
        }
    }, [isOpen, course]);

    // Fetch preview when rules change
    useEffect(() => {
        if (isOpen && formData.enrollment_method === 'automatic') {
            fetchPreview();
        }
    }, [isOpen, formData.lifecycle_stages, formData.roles, formData.company_wide, formData.matching_logic, formData.enrollment_method]);

    const fetchRoles = async () => {
        setLoadingRoles(true);
        try {
            const token = localStorage.getItem('token');
            const response = await api.get('/employees/roles', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAvailableRoles(response.data.roles || []);
        } catch (error) {
            console.error('Error fetching roles:', error);
        } finally {
            setLoadingRoles(false);
        }
    };

    const fetchPreview = async () => {
        setPreviewLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await api.post('/courses/preview-enrollments', {
                course_type: courseType,
                rules: {
                    matching_logic: formData.matching_logic,
                    lifecycle_stages: formData.lifecycle_stages,
                    roles: formData.roles,
                    company_wide: formData.company_wide
                }
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setPreviewCount(response.data.count || 0);
            setPreviewEmployees(response.data.employees || []);
        } catch (error) {
            console.error('Error fetching preview:', error);
            setPreviewCount(0);
        } finally {
            setPreviewLoading(false);
        }
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const handleLifecycleChange = (stage) => {
        setFormData(prev => ({
            ...prev,
            lifecycle_stages: prev.lifecycle_stages.includes(stage)
                ? prev.lifecycle_stages.filter(s => s !== stage)
                : [...prev.lifecycle_stages, stage]
        }));
    };

    const handleRoleToggle = (role) => {
        setFormData(prev => ({
            ...prev,
            roles: prev.roles.includes(role)
                ? prev.roles.filter(r => r !== role)
                : [...prev.roles, role]
        }));
    };

    const handleCompanyWide = (enabled) => {
        setFormData(prev => ({
            ...prev,
            company_wide: enabled,
            // Clear other selections if company-wide
            lifecycle_stages: enabled ? [] : prev.lifecycle_stages,
            roles: enabled ? [] : prev.roles
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            toast.error('Course name is required');
            return;
        }

        setLoading(true);
        const loadingToast = toast.loading(isEditing ? 'Updating course...' : 'Creating course...');

        try {
            const token = localStorage.getItem('token');

            // Build auto_enrollment_rules based on form
            const autoEnrollmentRules = formData.enrollment_method === 'automatic' ? {
                matching_logic: formData.matching_logic,
                lifecycle_stages: formData.lifecycle_stages,
                roles: formData.roles,
                teams: formData.teams,
                company_wide: formData.company_wide
            } : null;

            const payload = {
                name: formData.name,
                description: formData.description,
                duration_days: formData.duration_days,
                course_type: courseType,
                auto_enrollment_enabled: formData.enrollment_method === 'automatic',
                auto_enrollment_rules: autoEnrollmentRules
            };

            if (isEditing) {
                await api.put(`/courses/${course.id}`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Course updated successfully', { id: loadingToast });
            } else {
                await api.post('/courses', payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Course created successfully', { id: loadingToast });
            }

            onSuccess();
        } catch (error) {
            console.error('Error saving course:', error);
            toast.error(error.response?.data?.message || 'Failed to save course', { id: loadingToast });
        } finally {
            setLoading(false);
        }
    };

    const hasAnyTriggers = formData.lifecycle_stages.length > 0 ||
        formData.roles.length > 0 ||
        formData.company_wide;

    if (!isOpen) return null;

    const isPreboarding = courseType === 'preboarding';

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-0 md:p-4 transition-all">
            <div className="bg-card w-full h-full md:h-auto md:max-h-[90vh] md:max-w-3xl md:rounded-2xl shadow-2xl overflow-hidden animate-fadeIn flex flex-col">
                {/* Header */}
                <div className={`px-4 py-3 md:px-6 md:py-4 border-b border-border flex items-center justify-between safe-area-top ${isPreboarding ? 'bg-purple-50' : 'bg-blue-50'
                    }`}>
                    <div>
                        <h2 className="text-lg md:text-xl font-bold text-foreground">
                            {isEditing ? 'Edit Course' : 'Create New Course'}
                        </h2>
                        <p className="text-xs md:text-sm text-muted-foreground">
                            {isPreboarding ? 'Preboarding' : 'Onboarding'} Journey
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-muted-foreground hover:bg-card/50 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={(e) => e.preventDefault()} className="flex-1 overflow-y-auto">
                    <div className="p-4 md:p-6 space-y-5">
                        {/* Course Name */}
                        <div>
                            <label className="block text-sm font-medium text-muted-foreground mb-1">
                                Course Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder={isPreboarding ? "e.g., New Hire Preboarding Journey" : "e.g., Engineering Onboarding Program"}
                                className="w-full px-4 py-3 md:py-2.5 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                                required
                            />
                        </div>

                        {/* Description */}
                        <div>
                            <label className="block text-sm font-medium text-muted-foreground mb-1">
                                Description
                            </label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Briefly describe what this course covers..."
                                rows={2}
                                className="w-full px-4 py-3 md:py-2.5 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                            />
                        </div>

                        {/* Duration */}
                        <div>
                            <label className="block text-sm font-medium text-muted-foreground mb-1">
                                Duration (days)
                            </label>
                            <div className="flex items-center gap-3">
                                <input
                                    type="number"
                                    min="1"
                                    max="365"
                                    value={formData.duration_days}
                                    onChange={(e) => setFormData({ ...formData, duration_days: parseInt(e.target.value) || 30 })}
                                    className="w-32 px-4 py-3 md:py-2.5 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                                />
                                <span className="text-sm text-muted-foreground">Days from start date</span>
                            </div>
                        </div>

                        <div className="border-t border-border pt-5">
                            <h3 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
                                <Briefcase className="w-4 h-4" />
                                Enrollment Settings
                            </h3>

                            {/* Enrollment Method */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, enrollment_method: 'manual' })}
                                    className={`p-3 rounded-xl border-2 text-left transition-all ${formData.enrollment_method === 'manual'
                                        ? 'border-primary bg-primary/5'
                                        : 'border-border hover:border-border'
                                        }`}
                                >
                                    <div className="font-medium text-foreground mb-1">Manual Enrollment</div>
                                    <p className="text-xs text-muted-foreground">Assign employees manually to this course</p>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, enrollment_method: 'automatic' })}
                                    className={`p-3 rounded-xl border-2 text-left transition-all ${formData.enrollment_method === 'automatic'
                                        ? 'border-primary bg-primary/5'
                                        : 'border-border hover:border-border'
                                        }`}
                                >
                                    <div className="font-medium text-foreground mb-1">Automatic Enrollment</div>
                                    <p className="text-xs text-muted-foreground">Enroll based on rules (role, team, etc.)</p>
                                </button>
                            </div>

                            {/* Automation Rules */}
                            {formData.enrollment_method === 'automatic' && (
                                <div className="space-y-3 animate-in slide-in-from-top-2 duration-200">
                                    <div className="p-3 bg-blue-50 text-blue-800 rounded-lg text-sm border border-blue-100 flex items-start gap-2">
                                        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                        <p>Employees matching {formData.matching_logic === 'any' ? 'ANY' : 'ALL'} of the following criteria will be automatically enrolled.</p>
                                    </div>

                                    {/* Logic Selector (Any/All) */}
                                    <div className="flex bg-muted p-1 rounded-lg w-fit">
                                        <button
                                            type="button"
                                            onClick={() => setFormData({ ...formData, matching_logic: 'any' })}
                                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${formData.matching_logic === 'any'
                                                ? 'bg-card shadow text-foreground'
                                                : 'text-muted-foreground hover:text-muted-foreground'
                                                }`}
                                        >
                                            Match Any (OR)
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setFormData({ ...formData, matching_logic: 'all' })}
                                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${formData.matching_logic === 'all'
                                                ? 'bg-card shadow text-foreground'
                                                : 'text-muted-foreground hover:text-muted-foreground'
                                                }`}
                                        >
                                            Match All (AND)
                                        </button>
                                    </div>

                                    {/* Triggers Accordion */}
                                    <div className="border border-border rounded-xl overflow-hidden divide-y divide-border">
                                        {/* Lifecycle Stage Trigger */}
                                        <div>
                                            <button
                                                type="button"
                                                onClick={() => toggleSection('lifecycle')}
                                                className="w-full p-4 flex items-center justify-between hover:bg-muted transition-colors"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="p-2 bg-green-100 rounded-lg">
                                                        <Clock className="w-4 h-4 text-green-600" />
                                                    </div>
                                                    <div className="text-left">
                                                        <span className="font-medium text-foreground">Lifecycle Stage</span>
                                                        {formData.lifecycle_stages.length > 0 && (
                                                            <p className="text-sm text-green-600">{formData.lifecycle_stages.length} selected</p>
                                                        )}
                                                    </div>
                                                </div>
                                                {expandedSections.lifecycle ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
                                            </button>
                                            {expandedSections.lifecycle && (
                                                <div className="px-4 pb-4">
                                                    <div className="space-y-2">
                                                        {['onboarding', 'preboarding', 'probation', 'regular'].map((stage) => (
                                                            <label key={stage} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={formData.lifecycle_stages.includes(stage)}
                                                                    onChange={() => handleLifecycleChange(stage)}
                                                                    className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                                                                />
                                                                <span className="text-sm text-muted-foreground capitalize">{stage}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Roles Trigger */}
                                        <div>
                                            <button
                                                type="button"
                                                onClick={() => toggleSection('roles')}
                                                className="w-full p-4 flex items-center justify-between hover:bg-muted transition-colors"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="p-2 bg-blue-100 rounded-lg">
                                                        <Briefcase className="w-4 h-4 text-blue-600" />
                                                    </div>
                                                    <div className="text-left">
                                                        <span className="font-medium text-foreground">Job Roles</span>
                                                        {formData.roles.length > 0 && (
                                                            <p className="text-sm text-blue-600">{formData.roles.length} selected</p>
                                                        )}
                                                    </div>
                                                </div>
                                                {expandedSections.roles ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
                                            </button>
                                            {expandedSections.roles && (
                                                <div className="px-4 pb-4">
                                                    {loadingRoles ? (
                                                        <div className="flex items-center justify-center py-4">
                                                            <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />
                                                        </div>
                                                    ) : availableRoles.length === 0 ? (
                                                        <p className="text-sm text-muted-foreground py-2">No roles found. Create roles in Settings.</p>
                                                    ) : (
                                                        <div className="space-y-2 max-h-40 overflow-y-auto">
                                                            {availableRoles.map((role) => (
                                                                <label key={role.id || role.name} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted cursor-pointer">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={formData.roles.includes(role.name)}
                                                                        onChange={() => handleRoleToggle(role.name)}
                                                                        className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                                                                    />
                                                                    <span className="text-sm text-muted-foreground">{role.name}</span>
                                                                </label>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {/* Company-Wide Trigger */}
                                        <div>
                                            <button
                                                type="button"
                                                onClick={() => toggleSection('companyWide')}
                                                className="w-full p-4 flex items-center justify-between hover:bg-muted transition-colors"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="p-2 bg-purple-100 rounded-lg">
                                                        <Globe className="w-4 h-4 text-purple-600" />
                                                    </div>
                                                    <div className="text-left">
                                                        <span className="font-medium text-foreground">Company-Wide</span>
                                                        {formData.company_wide && (
                                                            <p className="text-sm text-purple-600">Enabled - All employees</p>
                                                        )}
                                                    </div>
                                                </div>
                                                {expandedSections.companyWide ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
                                            </button>
                                            {expandedSections.companyWide && (
                                                <div className="px-4 pb-4">
                                                    <label className="flex items-start gap-3 p-3 rounded-lg border-2 border-yellow-200 bg-yellow-50 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={formData.company_wide}
                                                            onChange={(e) => handleCompanyWide(e.target.checked)}
                                                            className="mt-1 w-4 h-4 text-primary border-border rounded focus:ring-primary"
                                                        />
                                                        <div>
                                                            <div className="flex items-center gap-2">
                                                                <span className="font-medium text-foreground">Enroll all employees</span>
                                                                <span className="px-2 py-0.5 bg-yellow-200 text-yellow-800 text-xs font-semibold rounded">
                                                                    <AlertTriangle className="w-3 h-3 inline mr-1" />
                                                                    Warning
                                                                </span>
                                                            </div>
                                                            <p className="text-sm text-muted-foreground mt-1">
                                                                This will enroll everyone in the company when they match the lifecycle stage.
                                                            </p>
                                                        </div>
                                                    </label>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Enrollment Preview */}
                                    {hasAnyTriggers && (
                                        <div className="p-4 bg-primary/5 border-t border-primary/20 rounded-lg">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <h4 className="font-medium text-foreground">Enrollment Preview</h4>
                                                    <p className="text-sm text-muted-foreground">
                                                        {previewLoading ? (
                                                            <span className="flex items-center gap-1">
                                                                <Loader2 className="w-3 h-3 animate-spin" />
                                                                Calculating...
                                                            </span>
                                                        ) : (
                                                            <>
                                                                <span className="font-bold text-primary">{previewCount}</span> existing employees will be enrolled immediately
                                                            </>
                                                        )}
                                                    </p>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPreviewModal(true)}
                                                    disabled={previewLoading || previewCount === 0}
                                                    className="flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary-hover disabled:text-muted-foreground disabled:cursor-not-allowed transition-colors"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                    Preview List
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </form>

                {/* Footer */}
                <div className="px-4 py-3 md:px-6 md:py-4 border-t border-border bg-muted flex items-center justify-end gap-3 safe-area-bottom">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-muted-foreground hover:bg-muted rounded-lg font-medium transition-colors text-sm md:text-base"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={loading || !formData.name.trim()}
                        className={`flex items-center gap-2 px-5 py-2 rounded-lg font-medium transition-colors text-sm md:text-base ${loading || !formData.name.trim()
                            ? 'bg-muted text-muted-foreground cursor-not-allowed'
                            : 'bg-primary hover:bg-primary-hover text-white'
                            }`}
                    >
                        <Save className="w-4 h-4" />
                        {isEditing ? 'Save Changes' : 'Create Course'}
                    </button>
                </div>
            </div>

            {/* Preview Modal */}
            {showPreviewModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-0 md:p-4">
                    <div className="bg-card w-full h-full md:rounded-xl md:shadow-2xl md:max-w-md md:w-full md:h-auto md:max-h-[70vh] overflow-hidden flex flex-col">
                        <div className="px-4 py-3 md:px-5 md:py-4 border-b border-border flex items-center justify-between safe-area-top">
                            <h3 className="font-bold text-foreground">Employees to Enroll ({previewCount})</h3>
                            <button
                                onClick={() => setShowPreviewModal(false)}
                                className="p-1.5 text-muted-foreground hover:bg-muted rounded-lg"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="p-4 overflow-y-auto flex-1 md:max-h-[calc(70vh-120px)]">
                            {previewEmployees.length === 0 ? (
                                <p className="text-center text-muted-foreground py-8">No employees match current criteria</p>
                            ) : (
                                <div className="space-y-2">
                                    {previewEmployees.map((emp) => (
                                        <div key={emp.id} className="flex items-center gap-3 p-2 rounded-lg bg-muted">
                                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium text-sm">
                                                {emp.first_name?.[0]}{emp.last_name?.[0]}
                                            </div>
                                            <div>
                                                <p className="font-medium text-foreground text-sm">{emp.first_name} {emp.last_name}</p>
                                                <p className="text-xs text-muted-foreground">{emp.position || emp.role_name || 'No role'}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="px-4 py-3 md:px-5 border-t border-border bg-muted safe-area-bottom">
                            <button
                                onClick={() => setShowPreviewModal(false)}
                                className="w-full py-2 bg-muted hover:bg-muted/80 text-muted-foreground rounded-lg font-medium transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CourseCreationModal;
